import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWebSocket } from '../context/WebSocketContext';
import { useAuth } from '../context/AuthContext';
import { Phone, PhoneOff, Video } from 'lucide-react';
import axios from 'axios';
import { resolveAvatarUrl } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const createRingtone = () => {
  let audioContext = null;
  let intervalId = null;
  let isPlaying = false;

  const playRing = () => {
    if (!audioContext || audioContext.state === 'closed') {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioContext.state === 'suspended') audioContext.resume();
    const now = audioContext.currentTime;
    const playTone = (freq, start, dur, vol = 0.3) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + start);
      gain.gain.setValueAtTime(vol, now + start);
      gain.gain.exponentialRampToValueAtTime(0.001, now + start + dur);
      osc.connect(gain);
      gain.connect(audioContext.destination);
      osc.start(now + start);
      osc.stop(now + start + dur);
    };
    playTone(440, 0, 0.15, 0.35);
    playTone(520, 0.18, 0.15, 0.35);
    playTone(440, 0.5, 0.15, 0.35);
    playTone(520, 0.68, 0.15, 0.35);
  };

  return {
    start: () => {
      if (isPlaying) return;
      isPlaying = true;
      playRing();
      intervalId = setInterval(playRing, 2000);
    },
    stop: () => {
      isPlaying = false;
      if (intervalId) { clearInterval(intervalId); intervalId = null; }
      if (audioContext && audioContext.state !== 'closed') {
        audioContext.close().catch(() => {});
        audioContext = null;
      }
    }
  };
};

const IncomingCallModal = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { incomingCall, setIncomingCall } = useWebSocket();
  const ringtoneRef = useRef(null);
  
  // Swipe state
  const [swipeX, setSwipeX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startX = useRef(0);
  const trackRef = useRef(null);
  const THRESHOLD = 80;

  useEffect(() => {
    if (incomingCall) {
      ringtoneRef.current = createRingtone();
      ringtoneRef.current.start();
      if (navigator.vibrate) {
        const vibrateInterval = setInterval(() => {
          navigator.vibrate([500, 200, 500, 200, 500]);
        }, 2000);
        ringtoneRef.current._vibrateInterval = vibrateInterval;
      }
    }
    return () => {
      if (ringtoneRef.current) {
        ringtoneRef.current.stop();
        if (ringtoneRef.current._vibrateInterval) clearInterval(ringtoneRef.current._vibrateInterval);
        if (navigator.vibrate) navigator.vibrate(0);
        ringtoneRef.current = null;
      }
    };
  }, [incomingCall]);

  const stopRingtone = useCallback(() => {
    if (ringtoneRef.current) {
      ringtoneRef.current.stop();
      if (ringtoneRef.current._vibrateInterval) clearInterval(ringtoneRef.current._vibrateInterval);
      if (navigator.vibrate) navigator.vibrate(0);
      ringtoneRef.current = null;
    }
  }, []);

  const answerCall = useCallback(async () => {
    if (!incomingCall) return;
    stopRingtone();
    const { call_id, from_user, call_type } = incomingCall;
    try {
      await axios.post(`${API}/calls/${call_id}/answer`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch {}
    setIncomingCall(null);
    navigate(`/call/${call_id}?type=${call_type}&userId=${from_user.id}&incoming=true`);
  }, [incomingCall, token, navigate, setIncomingCall, stopRingtone]);

  const rejectCall = useCallback(async () => {
    if (!incomingCall) return;
    stopRingtone();
    const { call_id } = incomingCall;
    try {
      await axios.post(`${API}/calls/${call_id}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch {}
    setIncomingCall(null);
  }, [incomingCall, token, setIncomingCall, stopRingtone]);

  // Touch/mouse handlers for swipe
  const onStart = (e) => {
    setIsDragging(true);
    startX.current = e.touches ? e.touches[0].clientX : e.clientX;
  };

  const onMove = (e) => {
    if (!isDragging) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const diff = clientX - startX.current;
    setSwipeX(Math.max(-(THRESHOLD + 20), Math.min(THRESHOLD + 20, diff)));
  };

  const onEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (swipeX > THRESHOLD) {
      answerCall();
    } else if (swipeX < -THRESHOLD) {
      rejectCall();
    }
    setSwipeX(0);
  };

  if (!incomingCall) return null;
  const { call_type, from_user } = incomingCall;
  const callerName = from_user?.display_name || 'Unknown';
  const callerAvatar = from_user?.avatar_url;

  // Dynamic colors based on swipe direction
  const swipeProgress = swipeX / THRESHOLD;
  const isSwipingAccept = swipeX > 20;
  const isSwipingDecline = swipeX < -20;

  return (
    <div className="fixed inset-0 z-[200] flex flex-col" data-testid="incoming-call-modal">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-[#0a0a1a] to-gray-900" />
      
      {/* Pulse rings */}
      <div className="absolute inset-0 flex items-center justify-center" style={{ top: '-15%' }}>
        <div className="w-44 h-44 rounded-full border border-[#D4AF37]/10 animate-ping" style={{ animationDuration: '2s' }} />
      </div>
      <div className="absolute inset-0 flex items-center justify-center" style={{ top: '-15%' }}>
        <div className="w-56 h-56 rounded-full border border-[#D4AF37]/5 animate-ping" style={{ animationDuration: '3s' }} />
      </div>

      {/* Content */}
      <div className="relative flex flex-col items-center justify-between flex-1 pt-16 pb-[max(2rem,env(safe-area-inset-bottom))] px-6">
        {/* Caller info */}
        <div className="flex flex-col items-center">
          <p className="text-gray-400 text-sm mb-6 tracking-wider uppercase">
            Incoming {call_type} call
          </p>
          
          <div className="relative mb-6">
            <div className="w-28 h-28 rounded-full overflow-hidden ring-4 ring-[#D4AF37]/30 shadow-2xl shadow-[#D4AF37]/20">
              {callerAvatar ? (
                <img 
                  src={resolveAvatarUrl(callerAvatar)} 
                  alt={callerName}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                />
              ) : null}
              <div 
                className="w-full h-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center text-white font-bold text-4xl"
                style={callerAvatar ? { display: 'none' } : {}}
              >
                {callerName.charAt(0).toUpperCase()}
              </div>
            </div>
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-gray-900 animate-pulse" />
          </div>
          
          <h1 className="text-white text-2xl font-semibold mb-1" data-testid="caller-name">
            {callerName}
          </h1>
          <div className="flex items-center gap-2 text-gray-400">
            {call_type === 'video' ? <Video size={16} /> : <Phone size={16} />}
            <span className="text-sm">ChatBuzzz {call_type} call</span>
          </div>
        </div>

        {/* Bottom action area - unified for all platforms */}
        <div className="w-full max-w-sm">
          {/* Tap buttons */}
          <div className="flex items-center justify-between mb-6">
            <button
              data-testid="reject-incoming-call-btn"
              onClick={rejectCall}
              className="flex flex-col items-center gap-2"
            >
              <div className="w-16 h-16 rounded-full bg-red-500/20 border-2 border-red-500 flex items-center justify-center active:scale-90 hover:bg-red-500/40 transition-all">
                <PhoneOff size={26} className="text-red-400" />
              </div>
              <span className="text-red-400 text-xs font-medium">Decline</span>
            </button>

            <div className="flex flex-col items-center gap-1">
              <span className="text-gray-500 text-[10px] uppercase tracking-widest">or swipe</span>
            </div>

            <button
              data-testid="answer-incoming-call-btn"
              onClick={answerCall}
              className="flex flex-col items-center gap-2"
            >
              <div className="w-16 h-16 rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center active:scale-90 hover:bg-green-500/40 transition-all">
                {call_type === 'video' ? <Video size={26} className="text-green-400" /> : <Phone size={26} className="text-green-400" />}
              </div>
              <span className="text-green-400 text-xs font-medium">Accept</span>
            </button>
          </div>

          {/* Swipe track */}
          <div 
            ref={trackRef}
            className="relative h-16 rounded-full bg-gray-800/80 backdrop-blur-sm overflow-hidden border border-gray-700/50 select-none touch-none"
            onTouchStart={onStart}
            onTouchMove={onMove}
            onTouchEnd={onEnd}
            onMouseDown={onStart}
            onMouseMove={onMove}
            onMouseUp={onEnd}
            onMouseLeave={() => { if (isDragging) onEnd(); }}
            data-testid="swipe-track"
          >
            {/* Decline zone glow */}
            <div 
              className="absolute left-0 top-0 bottom-0 w-16 flex items-center justify-center transition-opacity duration-200"
              style={{ opacity: isSwipingDecline ? Math.min(1, Math.abs(swipeProgress)) : 0.3 }}
            >
              <PhoneOff size={18} className="text-red-400" />
            </div>

            {/* Accept zone glow */}
            <div 
              className="absolute right-0 top-0 bottom-0 w-16 flex items-center justify-center transition-opacity duration-200"
              style={{ opacity: isSwipingAccept ? Math.min(1, swipeProgress) : 0.3 }}
            >
              {call_type === 'video' ? <Video size={18} className="text-green-400" /> : <Phone size={18} className="text-green-400" />}
            </div>

            {/* Animated arrows */}
            {!isDragging && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="flex items-center gap-1 text-gray-500">
                  <span className="text-xs animate-pulse" style={{ animationDelay: '0.4s' }}>&laquo;</span>
                  <span className="text-xs animate-pulse" style={{ animationDelay: '0.2s' }}>&laquo;</span>
                  <span className="text-[10px] mx-2 text-gray-500/70">SLIDE</span>
                  <span className="text-xs animate-pulse" style={{ animationDelay: '0.2s' }}>&raquo;</span>
                  <span className="text-xs animate-pulse" style={{ animationDelay: '0.4s' }}>&raquo;</span>
                </div>
              </div>
            )}

            {/* Draggable thumb */}
            <div
              className={`absolute top-1/2 left-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center shadow-lg cursor-grab active:cursor-grabbing transition-colors duration-150 ${
                swipeX > THRESHOLD ? 'bg-green-500 shadow-green-500/50' 
                : swipeX < -THRESHOLD ? 'bg-red-500 shadow-red-500/50' 
                : 'bg-gradient-to-r from-[#D4AF37] to-[#C5A059] shadow-[#D4AF37]/40'
              }`}
              style={{ transform: `translate(calc(-50% + ${swipeX}px), -50%)`, transition: isDragging ? 'none' : 'transform 0.3s ease-out' }}
            >
              <Phone size={20} className="text-white" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncomingCallModal;
